/**
 * Hook for managing photo uploads during area object creation.
 *
 * This hook:
 * - Generates a pre-determined entity ID for the new object
 * - Provides the property ID from context
 * - Filters blob URLs from photos array (they are stored separately)
 * - Tracks which blob URLs belong to this entity for offline display
 *
 * Usage:
 * ```tsx
 * const { entityId, propertyId, entityType } = useAreaPhotoUpload('technicalEquipment');
 *
 * // Pass to PhotosField for offline photo tracking
 * <PhotosField entityType={entityType} entityId={entityId} propertyId={propertyId} />
 *
 * // Filter blob URLs before calling create mutation
 * createMutation({ ...values, photosURLs });
 * ```
 */

import { useMemo } from 'react';
import { O } from '@mobily/ts-belt';
import { generateNodeId } from 'common/offline/generateNodeId';
import { usePropertyContext } from '../../properties/components/SingleProperty';
import { usePropertyRadioContext } from '../../properties/components/SingleRadioProperty';

// Radio domain node types (need to check both contexts)
const RADIO_NODE_TYPES = new Set([
  'baseStation',
  'radioUnit',
  'inputAntenna',
  'dropAntenna',
  'combiner',
  'coupler',
  'splitter',
  'repeater',
]);

export type AreaPhotoUploadContext = {
  /** Pre-generated entity ID for the new object */
  entityId: string;
  /** Entity type (e.g., 'technicalEquipment', 'switch', 'odf') */
  entityType: string;
  /** Property ID from context */
  propertyId: number;
};

/**
 * Hook for managing photo uploads during area object creation
 *
 * @param entityType - The type of area object being created (e.g., 'technicalEquipment', 'switch')
 * @returns Context for photo uploads including pre-generated entity ID
 */
export function useAreaPhotoUpload(entityType: string): AreaPhotoUploadContext {
  const { id: regularPropertyId } = usePropertyContext();
  const { id: radioPropertyId } = usePropertyRadioContext();

  // Use the appropriate property ID based on entity type
  const propertyId = useMemo(() => {
    const isRadioNode = RADIO_NODE_TYPES.has(entityType);
    const contextId = isRadioNode ? radioPropertyId : regularPropertyId;
    return O.getWithDefault(contextId, 0);
  }, [entityType, regularPropertyId, radioPropertyId]);

  // Generate a stable entity ID that persists for the lifetime of this hook
  // This ID will be used for both photo uploads and the create mutation
  const entityId = useMemo(() => generateNodeId(), []);

  return {
    entityId,
    entityType,
    propertyId,
  };
}

export default useAreaPhotoUpload;
